import React, { useState, useEffect, useCallback, useMemo } from 'react'; // ✅ useMemo añadido
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useClipboard } from '../context/ClipboardContext'; 

import {
  Box, Typography, Button, Card, CardContent, CardActions, Grid, TextField, 
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, 
  Autocomplete, Chip, Paper, IconButton, Tabs, Tab, Divider, Alert, Snackbar,
  InputAdornment, createFilterOptions
} from '@mui/material';
import { 
    Add as AddIcon, Search as SearchIcon, Edit as EditIcon, 
    Delete as DeleteIcon, ArrowBack as ArrowBackIcon,
    MedicalServices as MedicalServicesIcon, Science as ScienceIcon,
    AddShoppingCart as AddShoppingCartIcon, FlashOn as FlashIcon,
    Close as CloseIcon,
    Warning as WarningIcon 
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

// Hook de Debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const filter = createFilterOptions();

const SPECIALTIES = [
    { name: "Anestesiología", desc: "Manejo del dolor y anestesia." },
    { name: "Anatomía Patológica", desc: "Análisis de tejidos y biopsias." },
    { name: "Cardiología", desc: "Enfermedades del corazón y sistema circulatorio." },
    { name: "Cardiología Pediátrica", desc: "Corazón en niños." },
    { name: "Cirugía Bariátrica", desc: "Tratamiento quirúrgico de la obesidad." },
    { name: "Cirugía Bucal", desc: "Cirugía de boca y mandíbula." },
    { name: "Cirugía Cardiovascular", desc: "Corazón y grandes vasos." },
    { name: "Cirugía General", desc: "Abdomen, cuello, mama, tejidos blandos." },
    { name: "Cirugía Oncológica", desc: "Cirugía de tumores." },
    { name: "Cirugía Pediátrica", desc: "Cirugía en niños." },
    { name: "Cirugía Plástica", desc: "Reconstructiva y estética." },
    { name: "Cirugía Torácica", desc: "Cirugía del tórax (no corazón)." },
    { name: "Cirugía Vascular", desc: "Arterias y venas." },
    { name: "Dermatología", desc: "Piel, pelo y uñas." },
    { name: "Endocrinología", desc: "Hormonas y metabolismo." },
    { name: "Epidemiología", desc: "Control de enfermedades en poblaciones." },
    { name: "Fisiatría y Rehabilitación", desc: "Recuperación física." },
    { name: "Gastroenterología", desc: "Aparato digestivo." },
    { name: "Genética Médica", desc: "Enfermedades hereditarias." },
    { name: "Geriatría", desc: "Atención al adulto mayor." },
    { name: "Ginecología y Obstetricia", desc: "Salud mujer, embarazo y parto." },
    { name: "Hematología", desc: "Enfermedades de la sangre." },
    { name: "Infectología", desc: "Enfermedades infecciosas." },
    { name: "Inmunología Clínica", desc: "Sistema inmunológico y alergias." },
    { name: "Medicina Crítica", desc: "Cuidados Intensivos (UCI)." },
    { name: "Medicina de Emergencia", desc: "Atención aguda." },
    { name: "Medicina Familiar", desc: "Atención integral familia/comunidad." },
    { name: "Medicina Interna", desc: "Enfermedades del adulto no quirúrgicas." },
    { name: "Medicina Nuclear", desc: "Uso de sustancias radioactivas." },
    { name: "Medicina Ocupacional", desc: "Salud en el trabajo." },
    { name: "Medicina Paliativa", desc: "Alivio del sufrimiento." },
    { name: "Nefrología", desc: "Enfermedades de los riñones." },
    { name: "Neonatología", desc: "Recién nacidos." },
    { name: "Neumonología", desc: "Enfermedades respiratorias." },
    { name: "Neurocirugía", desc: "Cirugía de cerebro, médula y nervios." },
    { name: "Neurología", desc: "Sistema nervioso central y periférico." },
    { name: "Nutrición Clínica", desc: "Manejo nutricional especializado." },
    { name: "Odontología Pediátrica", desc: "Dental niños." },
    { name: "Oftalmología", desc: "Enfermedades de los ojos." },
    { name: "Oncología Médica", desc: "Tratamiento del cáncer." },
    { name: "Oncología Radioterápica", desc: "Radioterapia." },
    { name: "Otorrinolaringología", desc: "Oído, nariz, garganta." },
    { name: "Pediatría y Puericultura", desc: "Salud de niños y adolescentes." },
    { name: "Psiquiatría", desc: "Trastornos mentales." },
    { name: "Radiología e Imagenología", desc: "Diagnóstico por imágenes." },
    { name: "Reumatología", desc: "Enfermedades autoinmunes y articulares." },
    { name: "Salud Pública", desc: "Gestión de sistemas de salud." },
    { name: "Terapia Intensiva Pediátrica", desc: "UCI Pediátrica." },
    { name: "Traumatología y Ortopedia", desc: "Sistema musculoesquelético." },
    { name: "Urología", desc: "Sistema urinario y reprod. masculino." }
].sort((a, b) => a.name.localeCompare(b.name));

const SolicitudEstudiosPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToClipboard, clipboardItems, removeFromClipboard, clearClipboard } = useClipboard(); 
  const isAdmin = user?.role === 'administrador';

  const [allPatients, setAllPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [config, setConfig] = useState(null); // ✅ Nuevo estado config

  const [studies, setStudies] = useState([]);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400); 

  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState(0); 
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [notification, setNotification] = useState('');
  const [openCart, setOpenCart] = useState(false);
  
  const [luiatroDialog, setLuiatroDialog] = useState({ 
      open: false, content: '', onConfirm: null 
  });

  const [currentStudy, setCurrentStudy] = useState({ 
    id: null, name: '', description: '', specialty: '', preparation: '' 
  });

  // 1. CARGA INICIAL (PACIENTES Y CONFIGURACIÓN)
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }

      try {
        const headers = { 'Authorization': `Bearer ${token}` };
        // Cargamos ambas cosas al inicio
        const [patientsRes, configRes] = await Promise.all([
            fetch(`${API_URL}/api/patients`, { headers }),
            fetch(`${API_URL}/api/config`, { headers })
        ]);

        if (mounted) {
          if (patientsRes.ok) setAllPatients(await patientsRes.json());
          if (configRes.ok) setConfig(await configRes.json());
        }
      } catch (err) { console.error(err); } 
      finally { if (mounted) setIsInitialLoading(false); }
    };
    init();
    return () => { mounted = false; };
  }, [navigate]);

  // 2. LÓGICA DE FILTRADO DE ESPECIALIDADES (TARJETAS)
  const visibleSpecialties = useMemo(() => {
    if (!config || !config.favoriteSpecialties) return SPECIALTIES;
    try {
        const favs = typeof config.favoriteSpecialties === 'string' 
            ? JSON.parse(config.favoriteSpecialties) 
            : config.favoriteSpecialties;
        
        return (Array.isArray(favs) && favs.length > 0)
            ? SPECIALTIES.filter(s => favs.includes(s.name)) 
            : SPECIALTIES;
    } catch (e) { return SPECIALTIES; }
  }, [config]);

  // 3. FUNCIÓN DE BÚSQUEDA INTEGRADA CON FILTRO SPECS
const performSearch = useCallback(async (query, specialtyName) => {
    setLoading(true);
    try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        
        // Si hay una especialidad de tarjeta, mandamos esa y NADA MÁS
        if (specialtyName) {
            params.append('specialty', specialtyName);
        } else {
            // Si no hay tarjeta, mandamos las favoritas (specs) y el texto (q)
            if (query) params.append('q', query);
            if (config?.favoriteSpecialties) params.append('specs', config.favoriteSpecialties);
        }
        
        const res = await fetch(`${API_URL}/api/studies?${params.toString()}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if(res.ok) setStudies(await res.json());
    } catch(e) { console.error(e); }
    setLoading(false);
}, [config]);

  useEffect(() => {
      if (viewMode === 0) performSearch(debouncedSearch, null);
      else if (viewMode === 1 && selectedSpecialty) performSearch(null, selectedSpecialty.name);
  }, [debouncedSearch, selectedSpecialty, viewMode, performSearch]);

  const handleAddToRequest = (study) => {
      let fullDescription = study.description || "";
      if (study.preparation) fullDescription += `\n[PREP]: ${study.preparation}`;

      addToClipboard({
          id: study.id, 
          name: study.name, 
          commercialName: study.name, 
          activeIngredient: "Estudio Solicitado", 
          presentation: "ESTUDIO",
          dosage: fullDescription || "Ver detalles", 
          form: "Solicitud", 
          type: "study_request"
      });
      setNotification(`✅ ${study.name} agregado al carrito.`);
  };

  const handleRemoveFromCart = (cartId) => {
    if (removeFromClipboard) removeFromClipboard(cartId);
  };

  const handleSave = async () => {
      if(!currentStudy.name || !currentStudy.specialty) return alert("Campos obligatorios.");
      const isEditing = !!currentStudy.id;
      const url = isEditing ? `${API_URL}/api/studies/${currentStudy.id}` : `${API_URL}/api/studies`;
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type':'application/json', 'Authorization': `Bearer ${token}` };
      
      const payload = { ...currentStudy, createdBy: user?.id };
      if (isEditing) payload.id = currentStudy.id;

      try {
          const res = await fetch(url, { method: isEditing ? 'PUT' : 'POST', headers, body: JSON.stringify(payload) });
          if (res.ok) {
              setOpenForm(false);
              performSearch(debouncedSearch, selectedSpecialty?.name);
              setNotification(isEditing ? "Estudio actualizado" : "Estudio creado");
          }
      } catch (e) { alert("Error al guardar"); }
  };

  const handleDelete = (id) => {
    setLuiatroDialog({
        open: true,
        content: "¿Está seguro de que desea eliminar este estudio del catálogo?",
        onConfirm: async () => {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/api/studies/${id}`, { 
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            performSearch(debouncedSearch, selectedSpecialty?.name);
            setLuiatroDialog(prev => ({ ...prev, open: false }));
        }
    });
  };

  if (isInitialLoading) return <Box sx={{display:'flex', justifyContent:'center', mt:10}}><CircularProgress /></Box>;

  if (!selectedPatient) {
    return (
      <Dialog open={true} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>Selección de Paciente
          <IconButton onClick={() => navigate('/dashboard')} sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Autocomplete
            options={allPatients}
            getOptionLabel={(option) => `${option.name} ${option.lastName} (C.I.: ${option.patientId || option.cedula || 'S/C'})`}
            onChange={(event, newValue) => { if (newValue) setSelectedPatient(newValue); }}
            renderInput={(params) => <TextField {...params} label="Buscar Paciente" autoFocus />}
          />
        </DialogContent>
        <DialogActions><Button onClick={() => navigate('/patients')} color="secondary">Gestión de Pacientes</Button></DialogActions>
      </Dialog>
    );
  }

  return (
    <>
    <Box sx={{ p: 3 }}>
      <Box sx={{ display:'flex', justifyContent:'space-between', mb:2, alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" color="primary" sx={{ display:'flex', alignItems:'center', gap:1.5, fontWeight: 'bold' }}>
            <ScienceIcon fontSize="large"/> Catálogo de Estudios
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">Paciente: {selectedPatient.name} {selectedPatient.lastName}</Typography>
        </Box>
        <Box>
          <Button variant="outlined" onClick={() => setSelectedPatient(null)} startIcon={<SearchIcon />} sx={{ mr: 1 }}>Cambiar Paciente</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
              const defaultSpec = visibleSpecialties.length > 0 ? visibleSpecialties[0].name : '';
              setCurrentStudy({ id: null, name: '', description: '', specialty: (selectedSpecialty?.name || defaultSpec), preparation: '' });
              setOpenForm(true);
            }}>Nuevo Estudio</Button>
        </Box>
      </Box>

      <Tabs value={viewMode} onChange={(_, v) => { setViewMode(v); setSelectedSpecialty(null); setSearch(''); setStudies([]); }} sx={{ mb: 3 }}>
        <Tab label="Búsqueda General" icon={<SearchIcon />} iconPosition="start" />
        <Tab label="Por Especialidad" icon={<MedicalServicesIcon />} iconPosition="start" />
      </Tabs>

      {viewMode === 0 && (
        <Box sx={{ mb: 4 }}>
          <TextField fullWidth placeholder="Escriba el nombre del estudio..." value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon color="action" sx={{mr:1}}/> }}
          />
          {search.length < 2 && !loading && (
            <Box sx={{ textAlign:'center', mt: 6, opacity: 0.6 }}>
              <FlashIcon sx={{ fontSize: 60, mb: 1, color: '#1976d2' }} />
              <Typography variant="h6">Buscador Personalizado</Typography>
              <Typography>Escriba al menos 2 letras. Resultados basados en sus especialidades.</Typography>
            </Box>
          )}
        </Box>
      )}

      {viewMode === 1 && !selectedSpecialty && (
        <Grid container spacing={2}>
          {/* ✅ USAMOS visibleSpecialties EN LUGAR DE SPECIALTIES */}
          {visibleSpecialties.map((spec, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card onClick={() => setSelectedSpecialty(spec)} sx={{ cursor: 'pointer', height: '100%', borderLeft: '6px solid #1976d2', '&:hover': { bgcolor: '#f0f7ff' } }}>
                <CardContent>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>{spec.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{spec.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {(viewMode === 0 || (viewMode === 1 && selectedSpecialty)) && (
        <Box>
          {selectedSpecialty && (
            <Button startIcon={<ArrowBackIcon />} onClick={() => { setSelectedSpecialty(null); setStudies([]); }} sx={{ mb: 2 }}>Volver</Button>
          )}
          {loading ? <Box sx={{textAlign:'center', py:5}}><CircularProgress/></Box> : (
            <Grid container spacing={3}>
              {studies.map((study) => (
                <Grid item xs={12} sm={6} md={4} key={study.id}>
                  <Card sx={{ height: '100%', borderTop: '4px solid #4caf50' }}>
                    <CardContent>
                      <Chip label={study.specialty} size="small" color="primary" variant="outlined" sx={{ mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{study.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{study.description}</Typography>
                      <Button variant="contained" fullWidth startIcon={<AddShoppingCartIcon />} onClick={() => handleAddToRequest(study)} sx={{ mt: 2 }}>Agregar</Button>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', bgcolor: '#fafafa' }}>
                      <IconButton size="small" onClick={() => { setCurrentStudy(study); setOpenForm(true); }}><EditIcon /></IconButton>
                      {isAdmin && <IconButton size="small" color="error" onClick={() => handleDelete(study.id)}><DeleteIcon /></IconButton>}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{currentStudy.id ? 'Editar Estudio' : 'Nuevo Estudio'}</DialogTitle>
        <DialogContent dividers>
          <Autocomplete options={SPECIALTIES} getOptionLabel={(o) => o.name} value={SPECIALTIES.find(s => s.name === currentStudy.specialty) || null}
            onChange={(_, nv) => setCurrentStudy({ ...currentStudy, specialty: nv ? nv.name : '' })}
            renderInput={(p) => <TextField {...p} label="Especialidad" fullWidth sx={{mb:2, mt:1}} />}
          />
          <TextField label="Nombre" fullWidth sx={{mb:2}} value={currentStudy.name} onChange={e=>setCurrentStudy({...currentStudy, name:e.target.value})} />
          <TextField label="Descripción" multiline rows={3} fullWidth value={currentStudy.description} onChange={e=>setCurrentStudy({...currentStudy, description:e.target.value})} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!notification} autoHideDuration={2000} onClose={() => setNotification('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled">{notification}</Alert>
      </Snackbar>
    </Box>

    {/* CARRITO Y DIÁLOGOS RESTANTES IGUAL... */}
    <Dialog open={openCart} onClose={() => setOpenCart(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}><MedicalServicesIcon color="secondary"/> Estudios Seleccionados ({clipboardItems?.length || 0})</Box>
        <IconButton onClick={() => setOpenCart(false)}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {(!clipboardItems || clipboardItems.length === 0) ? (
          <Box sx={{ textAlign:'center', py:4, color:'text.secondary' }}>
            <AddShoppingCartIcon sx={{ fontSize:60, opacity:0.2, mb:2 }} />
            <Typography>No hay estudios agregados.</Typography>
          </Box>
        ) : (
          <Box>
            {clipboardItems.map(item => (
              <Box key={item.cartId} sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:1, p:1, borderBottom:'1px solid #eee' }}>
                <Box><Typography fontWeight="bold">{item.name}</Typography><Typography variant="caption" color="text.secondary">{item.dosage}</Typography></Box>
                <IconButton color="error" onClick={() => handleRemoveFromCart(item.cartId)}><DeleteIcon /></IconButton>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="error" startIcon={<DeleteIcon />} onClick={() => clearClipboard?.()} disabled={!clipboardItems?.length}>Vaciar Todo</Button>
        <Button variant="contained" onClick={() => navigate('/recipe-studies')}>Generar Solicitud</Button>
      </DialogActions>
    </Dialog>

    <Box sx={{ position:'fixed', bottom:30, right:30, zIndex:999 }}>
      <Button variant="contained" color="secondary" startIcon={<AddShoppingCartIcon />} onClick={() => setOpenCart(true)}>Carrito ({clipboardItems?.length || 0})</Button>
    </Box>

    <Dialog open={luiatroDialog.open} onClose={() => setLuiatroDialog(prev => ({...prev, open: false}))}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f', fontWeight: 'bold' }}>
            <WarningIcon /> Luiatro Health dice:
        </DialogTitle>
        <DialogContent dividers><Typography>{luiatroDialog.content}</Typography></DialogContent>
        <DialogActions>
            <Button onClick={() => setLuiatroDialog(prev => ({...prev, open: false}))}>Cancelar</Button>
            <Button variant="contained" color="error" onClick={luiatroDialog.onConfirm}>Confirmar</Button>
        </DialogActions>
    </Dialog>
    </>
  );
};

export default SolicitudEstudiosPage;